
import { useState, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import type { Pagination } from "../../services/stores/types";
import {
  GetStudentResponseDissertativeForStudent,
  GetStudentResponseDissertative,
  GetStudentResultDissertative,
  UpdateStudentResponseDissertative,
} from "./types/types";
import { studentResponseDissertativesAPI } from "./studentResponseDissertativesAPI";


export const useStudentResponseDissertatives = (token: string) => {
  const queryClient = useQueryClient();

  // ------ state de busca e paginação ------
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // key para lista (depende de search e paginação)
  const listQueryKey = [
    "studentResponsesDissertatives",
    { search, page: pagination.currentPage, perPage: pagination.itemsPerPage },
  ] as const;

  // ------ lista principal ------
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchList,
    isFetching,
  } = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      studentResponseDissertativesAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  // ------ item único para modal ------
  // Note: enabled: false, vamos disparar manualmente em getById
  useQuery({
    queryKey: ["studentResponse", token /* id dinâmico */],
    queryFn: ({ queryKey: [, , id] }) =>
      studentResponseDissertativesAPI.getById(id as string, token),
    enabled: false,
    staleTime: 0,
  });

  // ------ mutation de update ------
  const updateMutation = useMutation({
    mutationFn: ({ id, is_wrong }: UpdateStudentResponseDissertative) =>
      studentResponseDissertativesAPI.updateStudentResponseDissertative(
        id,
        { is_wrong },
        token
      ),
    onSuccess: (_updated, variables) => {
      // refaz lista e item modal
      refetchList();
      queryClient.invalidateQueries({ queryKey: listQueryKey });
      queryClient.invalidateQueries({
        queryKey: ["studentResponse"],
      });
    },
  });

  // ------ funções expostas ------

  const handleSearch = useCallback((term: string) => {
    setSearch(term);
    setPagination((p) => ({ ...p, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((p) => ({ ...p, currentPage: newPage }));
  }, []);

  const getStudentResponsesById = useCallback(
    async (id: string) => {
      // força fetch do item único
      return queryClient.fetchQuery({
        queryKey: ["studentResponse", token, id],
        queryFn: () => studentResponseDissertativesAPI.getById(id, token),
        staleTime: 0
      });
    },
    [queryClient, token]
  );

  const getResponsesByApplication = useCallback(
    (
      applicationId: string,
      page = 1,
      perPage = 10,
      searchTerm?: string
    ): Promise<GetStudentResponseDissertativeForStudent> => {
      return studentResponseDissertativesAPI.getAllByStudent(
        applicationId,
        token,
        page,
        perPage,
        searchTerm
      );
    },
    [token]
  );

  const getResponsesByDiagram = getResponsesByApplication;

  const getStudentResponsesByStudent = useCallback(
    (
      applicationId: string,
      studentId: string,
      page = 1,
      perPage = 10,
      status?: string,
      searchTerm?: string
    ): Promise<GetStudentResponseDissertative> => {
      return studentResponseDissertativesAPI.getAllStudentResponsesByStudent(
        applicationId,
        studentId,
        token,
        page,
        perPage,
        status,
        searchTerm
      );
    },
    [token]
  );

  const getStudentResults = useCallback(
    (
      applicationId: string,
      page = 1,
      perPage = 10,
      searchTerm?: string
    ): Promise<GetStudentResultDissertative> => {
      return studentResponseDissertativesAPI.getResults(
        applicationId,
        token,
        page,
        perPage,
        searchTerm
      );
    },
    [token]
  );

  const getStudentResultsByStudent = useCallback(
    (applicationId: string, studentId: string) => {
      return studentResponseDissertativesAPI.getStudentResultsByStudent(
        applicationId,
        studentId,
        token
      );
    },
    [token]
  );

  return {
    // lista
    studentResponsesDissertatives: data?.responses || [],
    pagination: data?.pagination || pagination,
    loading: isLoading || isFetching,
    error: isError ? error : null,
    search,
    setSearch: handleSearch,
    refetch: refetchList,
    changePage,

    // item único
    getStudentResponsesById,

    // outras buscas
    getResponsesByApplication,
    getResponsesByDiagram,
    getStudentResponsesByStudent,
    getStudentResults,
    getStudentResultsByStudent,

    // update
    updateStudentResponseDissertative: updateMutation.mutate,
  };
};
